"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDynamicFeature = void 0;
const React = __importStar(require("react"));
const oneaudi_os_utils_1 = require("@oneaudi/oneaudi-os-utils");
/**
 * Check if the script is already in the DOM
 * Since this script is used to set the initial state, we want to double check
 * that the script is indeed loaded (and not just placed) because
 * loading a Feature App multiple times could prematurely set the status to ready.
 * If Server Side Rendering is running then it's safe to say that the script is already loaded
 * @param src URL to the script
 */
const isScriptInitiallyAvailable = (src) => !(0, oneaudi_os_utils_1.isSSR)() &&
    !!globalThis.document?.querySelector?.(`script[src="${src}"][data-status="ready"]`);
const useDynamicFeature = (src, isCached) => {
    const canHydrate = isScriptInitiallyAvailable(src);
    const [status, setStatus] = React.useState(isCached || canHydrate ? 'ready' : 'idle');
    React.useEffect(() => {
        if (status === 'ready') {
            return;
        }
        // Allow falsy src value if waiting on other data needed for
        // constructing the script URL passed to this hook.
        if (!src) {
            setStatus('idle');
            return;
        }
        // Fetch existing script element by src
        // It may have been added by another instance of this hook
        let script = globalThis.document?.querySelector?.(`script[src="${src}"]`);
        if (!script && (0, oneaudi_os_utils_1.isBrowser)()) {
            // Create script tag and append it to DOM
            script = createScriptTag(src);
            // Add preload hints for these features
            addResourceHints(src);
            // Store status in attribute on script
            // This can be read by other instances of this hook
            const setAttributeFromEvent = (event) => {
                script.setAttribute('data-status', event.type === 'load' ? 'ready' : 'error');
            };
            script.addEventListener('load', setAttributeFromEvent);
            script.addEventListener('error', setAttributeFromEvent);
        }
        else {
            // Grab existing script status from attribute and set to state.
            setStatus(script.getAttribute('data-status'));
        }
        // Script event handler to update status in state
        // Note: Even if the script already exists we still need to add
        // event handlers to update the state for *this* hook instance.
        const setStateFromEvent = (event) => {
            // give the browser time to parse JS
            requestAnimationFrame(() => {
                setStatus(event.type === 'load' ? 'ready' : 'error');
            });
        };
        // Add event listeners
        script.addEventListener('load', setStateFromEvent);
        script.addEventListener('error', setStateFromEvent);
        // Remove event listeners on cleanup
        return () => {
            if (script) {
                script.removeEventListener('load', setStateFromEvent);
                script.removeEventListener('error', setStateFromEvent);
            }
        };
    }, [src] // Only re-run effect if script src changes
    );
    return status;
};
exports.useDynamicFeature = useDynamicFeature;
/**
 * Add resource hints to improve loading performance
 * This script is made to work with headless-browser-based SSR
 */
function addResourceHints(url) {
    if ((0, oneaudi_os_utils_1.isSSR)() &&
        // make sure we are in the browser
        (0, oneaudi_os_utils_1.isBrowser)()) {
        var res = document.createElement('link');
        res.rel = 'preload';
        res.as = 'script';
        res.href = url;
        document.head.appendChild(res);
    }
}
/**
 * Inject script tag in <head>.
 * Not to be executed in Node.js environment.
 * @param src
 * @returns
 */
function createScriptTag(src) {
    const script = document.createElement('script');
    script.src = src;
    // defer will avoid render blocking and will ensure scripts are loaded in specific order
    // this is important because we need to evaluate federated modules before the app starts injecting them
    script.defer = true;
    script.setAttribute('data-status', 'loading');
    try {
        // Add script to document head. Try to make it the first child so it loads before React
        document.head.insertBefore(script, document.head.firstChild);
    }
    catch (e) {
        // ... and if that failed, let's just append
        document.head.appendChild(script);
        console.warn('Could not inject script at the top of <head>. This can cause a race condition.');
    }
    return script;
}
